import { useState } from 'react';
import { MediaType } from '@spi/shared';
import type { ProjectMedia } from '@spi/shared';
import { useDeleteMedia } from '../../../api/media';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import toast from 'react-hot-toast';

interface Props {
  media: ProjectMedia[];
  canDelete?: boolean;
}

export function MediaGallery({ media, canDelete }: Props) {
  const deleteMutation = useDeleteMedia();
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const images = media.filter((m) => m.type === MediaType.IMAGE);
  const documents = media.filter((m) => m.type === MediaType.DOCUMENT);

  const handleConfirmDelete = async () => {
    if (deleteId == null) return;
    try {
      await deleteMutation.mutateAsync(deleteId);
      toast.success('File deleted');
    } catch {
      toast.error('Failed to delete');
    }
    setDeleteId(null);
  };

  if (media.length === 0) return null;

  return (
    <div className="space-y-4">
      {images.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-spi-slate mb-2">Images</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map((img) => (
              <div key={img.id} className="relative group">
                <button onClick={() => setExpandedImage(img.url)} className="w-full aspect-square rounded-lg overflow-hidden bg-spi-seed">
                  <img src={img.url} alt={img.originalName} className="w-full h-full object-cover" />
                </button>
                {canDelete && (
                  <button
                    onClick={() => setDeleteId(img.id)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    X
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {documents.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-spi-slate mb-2">Documents</h4>
          <div className="space-y-2">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-spi-parchment">
                <a href={doc.url} target="_blank" rel="noreferrer" className="text-sm text-spi-green hover:underline flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {doc.originalName}
                </a>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-spi-slate">{(doc.sizeBytes / 1024).toFixed(0)} KB</span>
                  {canDelete && (
                    <button onClick={() => setDeleteId(doc.id)} className="text-red-500 hover:text-red-700 text-xs">
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expanded image lightbox */}
      {expandedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setExpandedImage(null)}>
          <div className="absolute inset-0 bg-black/70" />
          <img src={expandedImage} alt="Expanded" className="relative max-w-[90vw] max-h-[90vh] object-contain rounded-lg" />
        </div>
      )}

      <ConfirmDialog
        open={deleteId != null}
        title="Delete File"
        message="Are you sure you want to delete this file? This action cannot be undone."
        confirmLabel="Delete File"
        danger
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
